import React, { useState } from 'react';
import { FileText, Download, Search, Filter, BookOpen, FileCheck, Shield, Users, Calendar, ExternalLink, Eye } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { useNavigate } from 'react-router-dom';
export function KnowledgeCenter() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const documents = [{
    id: 1,
    category: 'governance',
    title: 'Anggaran Dasar (AD) ATIOS',
    description: 'Dokumen anggaran dasar organisasi ATIOS yang mengatur struktur dan tujuan organisasi',
    fileSize: '2.5 MB',
    fileType: 'PDF',
    lastUpdated: 'December 15, 2024',
    version: 'v2.0',
    downloads: 245,
    icon: Shield
  }, {
    id: 2,
    category: 'governance',
    title: 'Anggaran Rumah Tangga (ART) ATIOS',
    description: 'Peraturan internal dan tata tertib organisasi ATIOS',
    fileSize: '1.8 MB',
    fileType: 'PDF',
    lastUpdated: 'December 15, 2024',
    version: 'v2.0',
    downloads: 198,
    icon: FileCheck
  }, {
    id: 3,
    category: 'governance',
    title: 'Struktur Organisasi ATIOS',
    description: 'Bagan struktur organisasi dan deskripsi tugas setiap posisi',
    fileSize: '1.2 MB',
    fileType: 'PDF',
    lastUpdated: 'January 1, 2025',
    version: 'v1.5',
    downloads: 312,
    icon: Users
  }, {
    id: 4,
    category: 'membership',
    title: 'Panduan Keanggotaan',
    description: 'Panduan lengkap tentang jenis keanggotaan, hak dan kewajiban anggota',
    fileSize: '3.2 MB',
    fileType: 'PDF',
    lastUpdated: 'November 20, 2024',
    version: 'v3.1',
    downloads: 456,
    icon: BookOpen
  }, {
    id: 5,
    category: 'membership',
    title: 'Formulir Pendaftaran Anggota',
    description: 'Template formulir pendaftaran anggota baru ATIOS',
    fileSize: '0.5 MB',
    fileType: 'DOCX',
    lastUpdated: 'October 10, 2024',
    version: 'v2.3',
    downloads: 523,
    icon: FileText
  }, {
    id: 6,
    category: 'policy',
    title: 'Kode Etik Anggota',
    description: 'Kode etik dan standar perilaku untuk anggota ATIOS',
    fileSize: '0.8 MB',
    fileType: 'PDF',
    lastUpdated: 'September 5, 2024',
    version: 'v1.2',
    downloads: 289,
    icon: Shield
  }, {
    id: 7,
    category: 'policy',
    title: 'Kebijakan Privasi Data',
    description: 'Kebijakan perlindungan data pribadi anggota ATIOS',
    fileSize: '1.1 MB',
    fileType: 'PDF',
    lastUpdated: 'August 15, 2024',
    version: 'v1.0',
    downloads: 167,
    icon: Shield
  }, {
    id: 8,
    category: 'event',
    title: 'Panduan Penyelenggaraan Event',
    description: 'Panduan lengkap untuk chapter dalam menyelenggarakan event ATIOS',
    fileSize: '2.8 MB',
    fileType: 'PDF',
    lastUpdated: 'July 20, 2024',
    version: 'v2.5',
    downloads: 234,
    icon: Calendar
  }, {
    id: 9,
    category: 'event',
    title: 'Template Proposal Event',
    description: 'Template proposal untuk pengajuan event chapter',
    fileSize: '0.6 MB',
    fileType: 'DOCX',
    lastUpdated: 'July 20, 2024',
    version: 'v1.8',
    downloads: 345,
    icon: FileText
  }, {
    id: 10,
    category: 'financial',
    title: 'Laporan Keuangan 2024',
    description: 'Laporan keuangan tahunan ATIOS periode 2024',
    fileSize: '4.5 MB',
    fileType: 'PDF',
    lastUpdated: 'December 31, 2024',
    version: 'v1.0',
    downloads: 178,
    icon: FileCheck
  }, {
    id: 11,
    category: 'financial',
    title: 'Panduan Pengelolaan Keuangan Chapter',
    description: 'Panduan pengelolaan keuangan untuk chapter ATIOS',
    fileSize: '1.9 MB',
    fileType: 'PDF',
    lastUpdated: 'June 10, 2024',
    version: 'v2.0',
    downloads: 156,
    icon: BookOpen
  }, {
    id: 12,
    category: 'training',
    title: 'Materi Training Leadership',
    description: 'Materi training kepemimpinan untuk pengurus chapter',
    fileSize: '5.2 MB',
    fileType: 'PDF',
    lastUpdated: 'May 15, 2024',
    version: 'v1.5',
    downloads: 267,
    icon: BookOpen
  }];
  const filteredDocuments = documents.filter(doc => {
    const matchesTab = activeTab === 'all' || doc.category === activeTab;
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) || doc.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });
  const stats = [{
    label: 'Total Documents',
    value: documents.length.toString(),
    icon: FileText
  }, {
    label: 'Categories',
    value: '6',
    icon: BookOpen
  }, {
    label: 'Total Downloads',
    value: '3.2K+',
    icon: Download
  }];
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'governance':
        return 'text-atios-primary';
      case 'membership':
        return 'text-success';
      case 'policy':
        return 'text-warning';
      case 'event':
        return 'text-error';
      case 'financial':
        return 'text-neutral-700';
      case 'training':
        return 'text-atios-secondary';
      default:
        return 'text-neutral-500';
    }
  };
  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'governance':
        return 'Governance';
      case 'membership':
        return 'Membership';
      case 'policy':
        return 'Policy';
      case 'event':
        return 'Event';
      case 'financial':
        return 'Financial';
      case 'training':
        return 'Training';
      default:
        return category;
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-3612">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-3613">
        {/* Header */}
        <div data-id="element-3614">
          <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-3615">
            Knowledge Center
          </h1>
          <p className="text-neutral-600" data-id="element-3616">
            Access organizational documents, guidelines, and resources
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3617">
          {stats.map((stat, i) => <Card key={i} data-id="element-3618">
              <CardContent className="p-6" data-id="element-3619">
                <div className="flex items-center gap-4" data-id="element-3620">
                  <div className="h-12 w-12 rounded-lg bg-atios-light flex items-center justify-center" data-id="element-3621">
                    <stat.icon className="h-6 w-6 text-atios-primary" data-id="element-3622" />
                  </div>
                  <div data-id="element-3623">
                    <p className="text-2xl font-semibold text-neutral-900" data-id="element-3624">
                      {stat.value}
                    </p>
                    <p className="text-sm text-neutral-600" data-id="element-3625">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4" data-id="element-3626">
          <div className="flex-1 relative" data-id="element-3627">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" data-id="element-3628" />
            <Input placeholder="Search documents..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-3629" />
          </div>
          <Button variant="secondary" data-id="element-3630">
            <Filter className="w-4 h-4 mr-2" data-id="element-3631" />
            Filter
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="all" className="w-full" data-id="element-3632">
          <TabsList className="bg-neutral-100 border border-neutral-200 flex-wrap h-auto" data-id="element-3633">
            <TabsTrigger value="all" onClick={() => setActiveTab('all')} data-id="element-3634">
              All Documents
            </TabsTrigger>
            <TabsTrigger value="governance" onClick={() => setActiveTab('governance')} data-id="element-3635">
              Governance
            </TabsTrigger>
            <TabsTrigger value="membership" onClick={() => setActiveTab('membership')} data-id="element-3636">
              Membership
            </TabsTrigger>
            <TabsTrigger value="policy" onClick={() => setActiveTab('policy')} data-id="element-3637">
              Policy
            </TabsTrigger>
            <TabsTrigger value="event" onClick={() => setActiveTab('event')} data-id="element-3638">
              Event
            </TabsTrigger>
            <TabsTrigger value="financial" onClick={() => setActiveTab('financial')} data-id="element-3639">
              Financial
            </TabsTrigger>
            <TabsTrigger value="training" onClick={() => setActiveTab('training')} data-id="element-3640">
              Training
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6" data-id="element-3641">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3642">
              {filteredDocuments.map(doc => {
              const categoryColor = getCategoryColor(doc.category);
              return <Card key={doc.id} className="hover:shadow-md transition-all" data-id="element-3643">
                    <CardHeader data-id="element-3644">
                      <div className="flex items-start justify-between mb-3" data-id="element-3645">
                        <div className={`h-12 w-12 rounded-lg bg-neutral-100 flex items-center justify-center ${categoryColor}`} data-id="element-3646">
                          <doc.icon className="h-6 w-6" data-id="element-3647" />
                        </div>
                        <Badge variant="default" className="text-xs" data-id="element-3648">
                          {getCategoryBadge(doc.category)}
                        </Badge>
                      </div>
                      <CardTitle className="text-base text-neutral-900 line-clamp-2" data-id="element-3649">
                        {doc.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4" data-id="element-3650">
                      <p className="text-sm text-neutral-600 line-clamp-2" data-id="element-3651">
                        {doc.description}
                      </p>

                      <div className="grid grid-cols-2 gap-2 text-xs text-neutral-600" data-id="element-3652">
                        <div data-id="element-3653">
                          <p className="text-neutral-500" data-id="element-3654">File Type</p>
                          <p className="font-medium text-neutral-900" data-id="element-3655">
                            {doc.fileType}
                          </p>
                        </div>
                        <div data-id="element-3656">
                          <p className="text-neutral-500" data-id="element-3657">Size</p>
                          <p className="font-medium text-neutral-900" data-id="element-3658">
                            {doc.fileSize}
                          </p>
                        </div>
                        <div data-id="element-3659">
                          <p className="text-neutral-500" data-id="element-3660">Version</p>
                          <p className="font-medium text-neutral-900" data-id="element-3661">
                            {doc.version}
                          </p>
                        </div>
                        <div data-id="element-3662">
                          <p className="text-neutral-500" data-id="element-3663">Downloads</p>
                          <p className="font-medium text-neutral-900" data-id="element-3664">
                            {doc.downloads}
                          </p>
                        </div>
                      </div>

                      <div className="text-xs text-neutral-500" data-id="element-3665">
                        Last updated: {doc.lastUpdated}
                      </div>

                      <div className="flex gap-2 pt-2" data-id="element-3666">
                        <Button variant="primary" size="sm" className="flex-1" onClick={() => alert('Mengunduh dokumen...')} data-id="element-3667">
                          <Download className="w-4 h-4 mr-2" data-id="element-3668" />
                          Download
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => navigate(`/documents/${doc.id}`)} data-id="element-3669">
                          <Eye className="w-4 h-4" data-id="element-3670" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Help Section */}
        <Card className="bg-gradient-to-r from-atios-light to-white border-atios-primary/20" data-id="element-3671">
          <CardContent className="p-8" data-id="element-3672">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-3673">
              <div data-id="element-3674">
                <h3 className="text-xl font-semibold text-neutral-900 mb-2" data-id="element-3675">
                  Need Help Finding a Document?
                </h3>
                <p className="text-neutral-600" data-id="element-3676">
                  Contact our support team for assistance with document access
                </p>
              </div>
              <Button size="lg" className="whitespace-nowrap" data-id="element-3677">
                Contact Support <ExternalLink className="w-4 h-4 ml-2" data-id="element-3678" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}